/**
 * The assignment matrix (PRD 10.6; Design Review V-8; Phase 28), shared by the read model behind
 * `GET /forecast/matrix` and the browser card, so the two cannot disagree about what a column
 * is, which tier a cell wears when its hours are of several, how the week is chosen, or what
 * the table exports.
 *
 * The matrix answers one question for one week: who is on what. Rows are people, columns are
 * the projects, retainers and deals with hours that week (plus the non-project baseline, which
 * is demand nobody can rebalance but which the row total has to include), a cell is the hours.
 * The row total is read against the person's capacity in the run's own threshold states — the
 * heatmap's cell for that person and week, field for field — which is what makes the two views
 * one set of numbers.
 *
 * Pure: no DOM, no I/O.
 */
import { addDays, mondayOf, weekWindows } from '../weeks.js';
import { col } from './table.js';
import { fillForTier } from './patterns.js';
import { typeSlot } from './palette.js';

/** The shared filters the matrix reads, in the API's names. The week is the card's own. */
export const MATRIX_PARAM_KEYS = Object.freeze(['run_id', 'dimension', 'entity', 'pipeline']);

/**
 * The four kinds of column, in the order the sketch draws them: committed work, then retainers,
 * then deals, then the baseline no one is assigned to. `status` is the record status each kind
 * wears (PRD Appendix A: solid border for committed work, dashed for an opportunity), and
 * `entity_type` the allocation rows a column of that kind is summed from.
 */
export const KIND_VOCABULARY = Object.freeze([
  Object.freeze({
    key: 'project',
    label: 'Project',
    plural: 'Projects',
    status: 'open',
    entity_type: 'project',
    hint: 'Open projects with hours this week — explicit assignments, or inferred where Workamajig has none',
  }),
  Object.freeze({
    key: 'recurring',
    label: 'Retainer',
    plural: 'Retainers',
    status: 'open',
    entity_type: 'recurring',
    hint: 'Recurring work forecast from its trailing baseline rather than a schedule',
  }),
  Object.freeze({
    key: 'opportunity',
    label: 'Deal',
    plural: 'Deals',
    status: 'opportunity',
    entity_type: 'opportunity',
    hint: 'Open HubSpot deals whose kickoff falls in or before this week, in the chosen pipeline mode',
  }),
  Object.freeze({
    key: 'nonproject',
    label: 'Non-project',
    plural: 'Non-project',
    status: 'nonproject',
    entity_type: 'nonproject',
    hint: 'Each person’s non-project baseline: meetings, admin and internal time, from their history',
  }),
]);
export const COLUMN_KINDS = Object.freeze(KIND_VOCABULARY.map((k) => k.key));
const KIND_BY_KEY = new Map(KIND_VOCABULARY.map((k) => [k.key, k]));

/** The key of the one column that is not a record: the non-project baseline. */
export const NONPROJECT_KEY = 'nonproject';

/** The key of the foot row holding demand nobody is named for yet. */
export const UNASSIGNED_KEY = 'unassigned';

/** How the columns may be ordered: by the week's hours (the default), by name, or by type. */
export const COLUMN_SORTS = Object.freeze(['hours', 'name', 'type']);

/** How the rows may be ordered: by name (the default), by allocation over capacity, or by hours. */
export const ROW_SORTS = Object.freeze(['name', 'utilization', 'hours']);

/** Confidence tiers, most to least confident (PRD 6.1). */
export const CONFIDENCE_ORDER = Object.freeze(['high', 'medium', 'low']);

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/** The record status a kind of column wears. */
export function statusOf(kind) {
  return KIND_BY_KEY.get(kind)?.status ?? 'open';
}

/** A kind's vocabulary entry, so the card and the export name it the same way. */
export function kindOf(key) {
  return KIND_BY_KEY.get(key) ?? null;
}

/**
 * The tier a cell wears when its hours are of several: the one with the most hours, ties to the
 * less confident — the rule the signals module applies to a week's contributors, restated here
 * so a cell and the signal that cites it agree. Null when there are no hours at all.
 * @param {{ high?: number, medium?: number, low?: number } | null | undefined} tiers
 */
export function confidenceDriver(tiers) {
  let best = null;
  for (const t of CONFIDENCE_ORDER) {
    const h = Number(tiers?.[t]) || 0;
    if (h > 0 && (best === null || h >= (Number(tiers[best]) || 0))) best = t;
  }
  return best;
}

/** An empty tier split, so every cell and column carries the same three keys. */
export function emptyTiers() {
  return { high: 0, medium: 0, low: 0 };
}

// ---------------------------------------------------------------------------------------------
// The week

/**
 * The weeks the card offers, from the shared window (`from`, `weeks`) clipped to the run's
 * horizon: a week the run never forecast is not a week anyone can ask about. `from` null means
 * the run decides, as everywhere else — the horizon start.
 * @param {{ from?: string | null, weeks?: number }} filters
 * @param {{ horizon_start: string, horizon_end: string, horizon_weeks?: number } | null} run
 * @returns {{ week_start: string, week_end: string }[]}
 */
export function weekOptions(filters, run) {
  if (!run?.horizon_start) return [];
  const horizonStart = mondayOf(run.horizon_start);
  const horizonEnd = run.horizon_end ?? addDays(horizonStart, 7 * (run.horizon_weeks ?? 26) - 1);
  let start = filters?.from ? mondayOf(filters.from) : horizonStart;
  if (start < horizonStart) start = horizonStart;
  const count = Number(filters?.weeks) > 0 ? Number(filters.weeks) : (run.horizon_weeks ?? 26);
  return weekWindows(start, count)
    .filter((w) => w.start <= horizonEnd)
    .map((w) => ({ week_start: w.start, week_end: w.end }));
}

/**
 * The week to draw: the one asked for if the window still holds it, else the first of the window
 * — this week, which is the one a traffic manager operates in.
 * @param {{ week_start: string }[]} options
 * @param {string | null | undefined} requested
 */
export function resolveWeek(options, requested) {
  if (requested && options.some((w) => w.week_start === requested)) return requested;
  return options[0]?.week_start ?? null;
}

// ---------------------------------------------------------------------------------------------
// Ordering

const label = (r) => String(r?.label ?? r?.name ?? '');

/** The column comparator for one sort key; ties always fall back to the name so the order is total. */
export function columnComparator(by = 'hours') {
  const byName = (a, b) => label(a).localeCompare(label(b)) || String(a.key).localeCompare(b.key);
  if (by === 'name') return byName;
  if (by === 'type')
    return (a, b) =>
      Number(a.canonical_type == null) - Number(b.canonical_type == null) ||
      String(a.canonical_type ?? '').localeCompare(String(b.canonical_type ?? '')) ||
      (b.hours ?? 0) - (a.hours ?? 0) ||
      byName(a, b);
  // hours: the biggest column first; the non-project column always last, because it is not a
  // record anyone can move work between — it is the row's own floor.
  return (a, b) =>
    Number(a.kind === NONPROJECT_KEY) - Number(b.kind === NONPROJECT_KEY) ||
    (b.hours ?? 0) - (a.hours ?? 0) ||
    byName(a, b);
}

/** The row comparator. A person with no capacity sorts after everyone with one under `utilization`. */
export function rowComparator(by = 'name') {
  const byName = (a, b) => label(a).localeCompare(label(b)) || String(a.key).localeCompare(b.key);
  if (by === 'utilization')
    return (a, b) => {
      const ua = a.total?.utilization;
      const ub = b.total?.utilization;
      if (ua === null || ua === undefined)
        return ub === null || ub === undefined ? byName(a, b) : 1;
      if (ub === null || ub === undefined) return -1;
      return ub - ua || byName(a, b);
    };
  if (by === 'hours')
    return (a, b) =>
      (b.total?.allocated_hours ?? 0) - (a.total?.allocated_hours ?? 0) || byName(a, b);
  return byName;
}

export function sortColumns(columns, by = 'hours') {
  return [...(columns ?? [])].sort(columnComparator(by));
}

export function sortRows(rows, by = 'name') {
  return [...(rows ?? [])].sort(rowComparator(by));
}

// ---------------------------------------------------------------------------------------------
// Model

const matches = (column, needle) =>
  [column.name, column.client, column.number, column.source_id]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(needle));

/**
 * Everything the card draws, from a `GET /forecast/matrix` response: the columns the controls
 * leave in, in the requested order, each with its type hue slot resolved; the rows in theirs,
 * each with its cells re-indexed to those columns and the hours the controls have hidden stated
 * beside the total they were hidden from; the role-only foot; and the totals.
 *
 * Filtering is done here, in the browser, and not by the API — the opposite of the timeline's
 * rule, for the opposite reason. The timeline's lane had to describe the bars on screen, so its
 * controls went to the server. Here the row total must NOT follow the controls: it is the
 * person's whole week against their capacity, the heatmap's figure, whatever columns are
 * showing. A person at 117% is at 117% whether or not the deals are ticked. So the response
 * carries every column, the card hides some, and `hidden_hours` on the row says what the eye
 * cannot see.
 *
 * `person` is the key of a pinned row: when it names a row in the response, the columns are cut
 * to the ones that person has hours in, so a reader following one individual across the week
 * does not scan past everybody else's work. Every other row keeps its total and says what the
 * cut hides, like any other control.
 *
 * @param {object} data  a `GET /forecast/matrix` response
 * @param {{ kinds?: string[], types?: string[], tiers?: string[], q?: string,
 *           person?: string | null, columnSort?: string, rowSort?: string,
 *           palette?: object[] }} [opts]
 */
export function matrixModel(
  data,
  {
    kinds = COLUMN_KINDS,
    types = [],
    tiers = [],
    q = '',
    person = null,
    columnSort = 'hours',
    rowSort = 'name',
    palette = [],
  } = {},
) {
  if (!data?.week_start) return null;
  const wanted = new Set(kinds?.length ? kinds : COLUMN_KINDS);
  const needle = String(q ?? '')
    .trim()
    .toLowerCase();

  const all = (data.columns ?? []).map((column, index) => ({ ...column, index }));
  const pinned = person ? (data.rows ?? []).find((r) => r.key === person) : null;
  const shown = all.filter(
    (c) =>
      wanted.has(c.kind) &&
      (!types?.length || types.includes(c.canonical_type ?? '')) &&
      (!tiers?.length || tiers.includes(c.confidence_tier)) &&
      (!needle || matches(c, needle)) &&
      (!pinned || (pinned.cells?.[c.index]?.hours ?? 0) > 0),
  );
  const columns = sortColumns(shown, columnSort).map((c) => ({
    ...c,
    slot: c.kind === NONPROJECT_KEY ? null : typeSlot(palette, c.canonical_type),
    status: c.status ?? statusOf(c.kind),
    fill: c.fill ?? fillForTier(c.confidence_tier),
  }));

  const reindex = (cells) =>
    columns.map((c) => {
      const cell = cells?.[c.index] ?? null;
      return cell ? { ...cell, fill: cell.fill ?? fillForTier(cell.confidence_tier) } : null;
    });
  const shownHours = (cells) => round2(cells.reduce((s, c) => s + (c?.hours ?? 0), 0));

  const rows = sortRows(data.rows ?? [], rowSort).map((row) => {
    const cells = reindex(row.cells);
    const shownHours_ = shownHours(cells);
    return {
      ...row,
      cells,
      shown_hours: shownHours_,
      hidden_hours: round2((row.total?.allocated_hours ?? 0) - shownHours_),
      cells_shown: cells.filter((c) => c && c.hours > 0).length,
      state: row.total?.capacity_hours > 0 ? (row.total?.band ?? 'none') : 'none',
    };
  });

  const un = data.unassigned ?? null;
  const unassigned = un
    ? (() => {
        const cells = reindex(un.cells);
        const s = shownHours(cells);
        return {
          ...un,
          key: UNASSIGNED_KEY,
          cells,
          shown_hours: s,
          hidden_hours: round2((un.hours ?? 0) - s),
        };
      })()
    : null;

  return {
    week_start: data.week_start,
    week_end: data.week_end,
    columns,
    columns_hidden: all.length - columns.length,
    rows,
    unassigned,
    totals: data.totals ?? null,
    thresholds: data.thresholds ?? {
      underload_below: 0.85,
      caution_above: 1,
      overloaded_above: 1.15,
    },
    withheld_count: data.withheld_count ?? 0,
    dimension: data.dimension ?? 'company',
    entity: data.entity ?? null,
    pipeline_mode: data.pipeline_mode ?? 'weighted',
    kinds: [...wanted],
    column_sort: columnSort,
    row_sort: rowSort,
  };
}

/**
 * The figures the card's header and notes state, counted over what is drawn. `rows_over` is the
 * gate's number — people whose whole week, not just the columns showing, is past the caution
 * threshold.
 */
export function matrixSummary(model) {
  const perKind = Object.fromEntries(COLUMN_KINDS.map((k) => [k, 0]));
  for (const c of model?.columns ?? []) perKind[c.kind] = (perKind[c.kind] ?? 0) + 1;
  const bands = { underload: 0, healthy: 0, caution: 0, overloaded: 0, none: 0 };
  let shown = 0;
  let hidden = 0;
  let peak = null;
  for (const row of model?.rows ?? []) {
    bands[row.state] = (bands[row.state] ?? 0) + 1;
    shown = round2(shown + row.shown_hours);
    hidden = round2(hidden + row.hidden_hours);
    const u = row.total?.utilization;
    if (u !== null && u !== undefined && (peak === null || u > peak.total.utilization)) peak = row;
  }
  return {
    rows: model?.rows?.length ?? 0,
    columns: model?.columns?.length ?? 0,
    columns_hidden: model?.columns_hidden ?? 0,
    per_kind: perKind,
    per_band: bands,
    rows_over: bands.caution + bands.overloaded,
    hours_shown: shown,
    hours_hidden: hidden,
    unassigned_hours: model?.unassigned?.hours ?? 0,
    unassigned_shown: model?.unassigned?.shown_hours ?? 0,
    withheld_count: model?.withheld_count ?? 0,
    peak,
  };
}

/** What a screen reader (and the gate) hears for one cell. */
export function cellLabel(row, column, cell) {
  const who = row?.label ?? 'Role-only';
  if (!cell || !(cell.hours > 0)) return `${who} on ${column.name}: no hours`;
  const tier = cell.confidence_tier ? `, ${cell.confidence_tier} confidence` : '';
  return `${who} on ${column.name}: ${cell.hours} h${tier}`;
}

// ---------------------------------------------------------------------------------------------
// Exports

/**
 * The table view's rows: one per person and column with hours, long form, so a cell can be
 * checked against the Workamajig assignment from the export rather than from a tooltip. The
 * role-only foot's cells come last, under the name `Role-only`.
 */
export function matrixExportRows(model) {
  const out = [];
  const push = (rowLabel, row, cells) => {
    for (const [i, cell] of cells.entries()) {
      if (!cell || !(cell.hours > 0)) continue;
      const column = model.columns[i];
      out.push({
        person: rowLabel,
        department: row?.entity?.department ?? null,
        kind: kindOf(column.kind)?.label ?? column.kind,
        name: column.name,
        client: column.client ?? null,
        number: column.number ?? column.source_id ?? null,
        canonical_type: column.canonical_type ?? null,
        hours: cell.hours,
        confidence_tier: cell.confidence_tier ?? null,
        fill: cell.fill,
        high_hours: cell.tiers?.high ?? 0,
        medium_hours: cell.tiers?.medium ?? 0,
        low_hours: cell.tiers?.low ?? 0,
        source_modes: (cell.source_modes ?? []).join(' '),
        services: (cell.services ?? []).join(' '),
        assignments: cell.assignments ?? 0,
        slipped: cell.slipped === true,
      });
    }
  };
  for (const row of model?.rows ?? []) push(row.label, row, row.cells);
  if (model?.unassigned) push('Role-only', null, model.unassigned.cells);
  return out;
}

/** The people's own export: one row per person with the week's total against capacity. */
export function rowExportRows(model) {
  return (model?.rows ?? []).map((row) => ({
    person: row.label,
    department: row.entity?.department ?? null,
    office: row.entity?.office ?? null,
    allocated_hours: row.total?.allocated_hours ?? 0,
    capacity_hours: row.total?.capacity_hours ?? 0,
    headroom_hours: row.total?.headroom_hours ?? 0,
    utilization: row.total?.utilization ?? null,
    band: row.total?.capacity_hours > 0 ? (row.total?.band ?? null) : null,
    shown_hours: row.shown_hours,
    hidden_hours: row.hidden_hours,
    high_confidence_hours: row.total?.high_confidence_hours ?? 0,
    nonproject_hours: row.total?.nonproject_hours ?? 0,
    pipeline_hours: row.total?.pipeline_hours ?? 0,
    columns_with_hours: row.cells_with_hours ?? 0,
  }));
}

/** The column heads' export: one row per column with its week total split by tier. */
export function columnExportRows(model) {
  return (model?.columns ?? []).map((c) => ({
    kind: kindOf(c.kind)?.label ?? c.kind,
    name: c.name,
    client: c.client ?? null,
    number: c.number ?? c.source_id ?? null,
    canonical_type: c.canonical_type ?? null,
    status: c.status,
    confidence_tier: c.confidence_tier ?? null,
    fill: c.fill,
    hours: c.hours,
    people_hours: c.people_hours,
    unassigned_hours: c.unassigned_hours,
    withheld_hours: c.withheld_hours,
    hours_total: c.hours_total,
    high_hours: c.tiers?.high ?? 0,
    medium_hours: c.tiers?.medium ?? 0,
    low_hours: c.tiers?.low ?? 0,
    people: c.people,
    slipped: c.slipped === true,
    burn_risk: c.burn_risk === true,
    stage_name: c.stage_name ?? null,
    probability_used: num(c.probability_used),
  }));
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// Specifications, not renderings: the screen decorates them with `withFormats` and the CSV
// writer reads the same list, so an export cannot hold a different set of columns from the
// table it came from.

/** One row per person and column: the cells of the grid, long format. */
export function matrixCellColumns() {
  return [
    col.text('person', 'Person'),
    col.text('department', 'Department'),
    col.text('kind', 'Kind'),
    col.text('name', 'Column'),
    col.text('client', 'Client'),
    col.text('number', 'Number'),
    col.text('canonical_type', 'Type'),
    col.hours('hours', 'Hours'),
    col.text('confidence_tier', 'Confidence'),
    col.text('fill', 'Fill'),
    col.hours('high_hours', 'High'),
    col.hours('medium_hours', 'Medium'),
    col.hours('low_hours', 'Low'),
    col.text('source_modes', 'Source'),
    col.text('services', 'Role'),
    col.count('assignments', 'Assignments'),
    col.flag('slipped', 'Slipped'),
  ];
}

/** One row per person: the row totals beside the grid. */
export function matrixPeopleColumns() {
  return [
    col.text('person', 'Person'),
    col.text('department', 'Department'),
    col.hours('allocated_hours', 'Allocated'),
    col.hours('capacity_hours', 'Capacity'),
    col.hours('headroom_hours', 'Headroom'),
    col.percent('utilization', 'Load'),
    col.text('band', 'State', { csv: (v) => v ?? '' }),
    col.hours('shown_hours', 'Shown'),
    col.hours('hidden_hours', 'Hidden'),
    col.hours('high_confidence_hours', 'High confidence'),
    col.hours('nonproject_hours', 'Non-project'),
    col.hours('pipeline_hours', 'Pipeline'),
    col.count('columns_with_hours', 'Columns'),
  ];
}

/** One row per column of the grid: the projects and deals the week is spent on. */
export function matrixColumnColumns() {
  return [
    col.text('kind', 'Kind'),
    col.text('name', 'Column'),
    col.text('client', 'Client'),
    col.text('number', 'Number'),
    col.text('canonical_type', 'Type'),
    col.text('status', 'Status'),
    col.text('confidence_tier', 'Confidence'),
    col.hours('hours', 'Hours'),
    col.hours('people_hours', 'Named people'),
    col.hours('unassigned_hours', 'Role-only'),
    col.hours('withheld_hours', 'Withheld'),
    col.hours('hours_total', 'Total'),
    col.count('people', 'People'),
    col.flag('slipped', 'Slipped'),
    col.flag('burn_risk', 'Burn risk'),
    col.text('stage_name', 'Stage'),
    col.percent('probability_used', 'Probability'),
  ];
}
